import {
  VentureBlockchainCreateDocument,
  VentureBlockchainDeleteDocument,
  VentureBlockchainGetByIdDocument,
  VentureBlockchainListDocument,
  VentureBlockchainPaginatedDocument,
  VentureBlockchainUpdateDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { PartialNullable } from "config";

import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";
import {
  IVentureBlockchainCreateVars,
  IVentureBlockchainListVars,
  IVentureBlockchainModel,
  IVentureBlockchainPaginatedResponse,
  IVentureBlockchainPaginatedVars,
  IVentureBlockchainUpdateVars,
  VentureBlockchainListResponse,
} from "./types";

export async function getVentureBlockchainPaginated(
  variables: IVentureBlockchainPaginatedVars
): Promise<IVentureBlockchainPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureBlockchainPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureBlockchainPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createVentureBlockchain(
  variables: IVentureBlockchainCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureBlockchainCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureBlockchain",
      fieldNames: ["ventureBlockchainPaginated", "ventureBlockchainList"],
    });

    return res?.data?.ventureBlockchainCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateVentureBlockchain(
  variables: IVentureBlockchainUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureBlockchainUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureBlockchain",
      id: variables.ventureBlockchainId.toString(),
      fieldNames: [
        "ventureBlockchainPaginated",
        "ventureBlockchainGetById",
        "ventureBlockchainList",
      ],
    });

    return res?.data?.ventureBlockchainUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getVentureBlockchain(
  ventureBlockchainId: number
): Promise<PartialNullable<IVentureBlockchainModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureBlockchainGetByIdDocument,
      variables: {
        ventureBlockchainId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureBlockchainGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteVentureBlockchain(
  ventureBlockchainId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureBlockchainDeleteDocument,
      variables: {
        ventureBlockchainId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureBlockchain",
      id: ventureBlockchainId.toString(),
      fieldNames: ["ventureBlockchainPaginated", "ventureBlockchainList"],
    });

    return Boolean(res?.data?.ventureBlockchainDelete);
  } catch (error) {
    throw error;
  }
}

export async function getVentureBlockchainList(
  variables: IVentureBlockchainListVars
): Promise<VentureBlockchainListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureBlockchainListDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureBlockchainList;
  } catch (error) {
    throw error;
  }
}
